<template>
  <div class="cancel-flow-discount">
    <img
      v-if="isAnnual"
      class="cancel-flow-discount__image"
      src="@/assets.app/images/18518_hero_annual_exp_cancel_save_flow.svg"
      width="335"
      height="220"
      alt=""
    />
    <img
      v-else
      class="cancel-flow-discount__image"
      src="@/assets.app/images/18518_hero_monthly_exp_cancel_save_flow.svg"
      width="270"
      height="247"
      alt=""
    />

    <h2 class="cancel-flow-discount__heading">{{ heading }}</h2>
    <p v-if="isAnnual" class="cancel-flow-discount__subheading">
      Pay for 9 months - get 12 months
    </p>
    <p class="cancel-flow-discount__price">{{ price }}</p>
    <p class="cancel-flow-discount__original">{{ originalPrice }}</p>
    <p class="cancel-flow-discount__disclaimer">{{ disclaimer }}</p>
  </div>

  <div class="cancel-flow-popup__footer">
    <AppButton
      :loading="applying"
      :disabled="applying"
      class="cancel-flow-popup__cta"
      @click="onAccept"
    >
      {{ acceptLabel }}
    </AppButton>
    <AppButton
      :danger="true"
      :disabled="applying"
      class="cancel-flow-popup__cta"
      @click="onDecline"
    >
      Continue Canceling
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'

import AppButton from '@/app/components/button/AppButton.vue'
import { BillingView } from '@/models/billing'
import { Sentry } from '@/services/sentry'
import { Mixpanel } from '@/analytics/mixpanel'

const props = defineProps<{
  billing: BillingView
}>()
const emit = defineEmits<{
  (e: 'accepted'): void
  (e: 'decline'): void
}>()

const applying = ref(false)

onMounted(() => {
  Mixpanel.trackCancelFlowStepShown(props.billing, 'discount')
  Mixpanel.trackCancelOfferShown(props.billing, null, 'discount')
})

const isAnnual = computed(() => props.billing.plan_id === 'plan_annual')

const heading = computed(() =>
  isAnnual.value
    ? 'Get 3 months free in your first year'
    : 'Get 50% off your first month',
)
const price = computed(() =>
  isAnnual.value ? 'For you $147.75/year' : 'Only $12/month',
)
const originalPrice = computed(() =>
  isAnnual.value ? 'Original price $197/year' : 'Original price $24/month',
)
const acceptLabel = computed(() =>
  isAnnual.value ? 'Get 3 bonus months' : 'Get 50% off',
)
const disclaimer = computed(() =>
  isAnnual.value
    ? 'Accepting this discount charges $147.75 today and extends your subscription for a year. After that, your subscription renews yearly at $197/year. Cancel anytime.'
    : 'Accepting this discount ends your trial and charges $12 for your first month. After that, your subscription renews monthly at $24/month. Cancel anytime.',
)

/**
 * Apply the discount registered on the intro step: it ends the trial and
 * charges the discounted amount.
 */
async function onAccept(): Promise<void> {
  Mixpanel.trackCancelFlowButtonClick(props.billing, 'discount', 'accept')
  try {
    applying.value = true
    await props.billing.applyCancelDiscount()
    Mixpanel.trackCancelOfferAccepted(props.billing, null, 'discount')
    emit('accepted')
  } catch (err) {
    Sentry.captureException(err)
    alert('Failed to apply the discount')
  } finally {
    applying.value = false
  }
}

// Declining continues to the reasons, it doesn't cancel the subscription.
function onDecline(): void {
  Mixpanel.trackCancelFlowButtonClick(
    props.billing,
    'discount',
    'continue_canceling',
  )
  Mixpanel.trackCancelOfferDeclined(props.billing, null, 'discount')
  emit('decline')
}
</script>
